import React, { useState } from "react";
import {
  Card,
  Avatar,
  Typography,
  Tag,
  Button,
  Upload,
  Space,
  Divider,
  Row,
  Col,
  Badge,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  BookOutlined,
  CameraOutlined,
  LogoutOutlined,
  EditOutlined,
  IdcardOutlined,
  TeamOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { signOut } from "firebase/auth";
import { auth } from "../database/firebase";
import { updateDocDatabase, uploadFileDatabse } from "../database";
import { openSuccesNotification } from "./Notifications/succesNotification";
import "./ModernProfile.css";

const { Title, Text } = Typography;

const ModernProfile = ({ user, materii = [], clase = [] }) => {
  const [uploading, setUploading] = useState(false);

  const handleSignOut = () => {
    signOut(auth);
    window.location.reload();
  };

  const handleSignatureUpload = async (info) => {
    if (info.file) {
      setUploading(true);
      try {
        const [link] = await uploadFileDatabse(
          [info.file],
          `semnaturi${user.uid}_`
        );

        await updateDocDatabase("users", user.uid, {
          semnatura: link,
        });

        if (user.type === "profesor") {
          await updateDocDatabase("profesori", user.uid, {
            semnatura: link,
          });
        }

        openSuccesNotification("Semnătura a fost încărcată cu succes!");
      } catch (error) {
        console.error("Error uploading signature:", error);
      }
      setUploading(false);
    }
  };

  const getDisplayName = () => {
    if (user.type === "elevi") {
      return `${user.numeDeFamilie} ${user.initiala} ${user.prenume}`;
    }
    return `${user.numeDeFamilie} ${user.prenume}`;
  };

  const getSubtitle = () => {
    if (user.type === "profesor" || user.type === "admin") {
      const materiiNames =
        user.selectedMaterii
          ?.map(
            (materieId) =>
              materii?.find((mat) => mat.id === materieId)?.numeMaterie
          )
          .filter(Boolean)
          .join(", ") || "Fără materii";
      return materiiNames;
    }

    if (user.type === "elevi") {
      const clasa = clase?.find((cls) => cls.id === user.clasa);
      if (user.clasa === "N/A") return "Nu este înscris";
      return clasa ? `${clasa.anClasa} ${clasa.identificator}` : "Fără clasă";
    }

    return "";
  };

  const getProfileColor = () => {
    switch (user.type) {
      case "admin":
        return "#003d82";
      case "profesor":
        return "#1890ff";
      case "elevi":
        return "#52c41a";
      default:
        return "#1890ff";
    }
  };

  const getAccountTypeLabel = () => {
    switch (user.type) {
      case "admin":
        return "Administrator";
      case "profesor":
        return "Profesor";
      case "elevi":
        return "Elev";
      default:
        return user.type;
    }
  };

  const renderProfileInfo = () => {
    const infoItems = [
      {
        icon: <UserOutlined />,
        label: "Nume complet",
        value: getDisplayName(),
      },
      ...(user.adresaEmail && !user.adresaEmail.includes("dobriceansoftware")
        ? [
            {
              icon: <MailOutlined />,
              label: "Email",
              value: user.adresaEmail,
            },
          ]
        : []),
      {
        icon: <PhoneOutlined />,
        label: "Telefon",
        value: user.numarTelefon,
      },
    ];

    // Add specific fields based on user type
    if (user.type === "elevi") {
      infoItems?.push(
        {
          icon: <IdcardOutlined />,
          label: "CNP",
          value: user.cnp,
        },
        {
          icon: <HomeOutlined />,
          label: "Domiciliu",
          value: user.domiciliu,
        },
        {
          icon: <BankOutlined />,
          label: "Bursă",
          value: user.bursa || "Nu",
        }
      );

      const filteredParintii =
        user.parintii?.filter(
          (email) => !email.includes("dobriceansoftware")
        ) || [];
      if (filteredParintii?.length > 0) {
        infoItems?.push({
          icon: <TeamOutlined />,
          label: "Email-uri părinți",
          value: (
            <Space wrap>
              {filteredParintii.map((email, index) => (
                <Tag key={index} color="blue">
                  {email}
                </Tag>
              ))}
            </Space>
          ),
        });
      }
    }

    return (
      <div className="profile-info-grid">
        {infoItems?.map((item, index) => (
          <div key={index} className="profile-info-item">
            <div className="info-icon" style={{ color: getProfileColor() }}>
              {item.icon}
            </div>
            <div className="info-content">
              <div className="info-label">{item.label}</div>
              <div className="info-value">{item.value}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="modern-profile">
      {/* Profile Header */}
      <Card className="profile-header-card">
        <div className="profile-header-content">
          <div className="profile-avatar-section">
            <Badge
              count={
                <div
                  className="profile-type-badge"
                  style={{ backgroundColor: getProfileColor() }}
                >
                  {getAccountTypeLabel()}
                </div>
              }
              offset={[0, 20]}
            >
              <Avatar
                size={120}
                src={user?.photoLink}
                icon={<UserOutlined />}
                className="profile-avatar"
                style={{ backgroundColor: getProfileColor() }}
              />
            </Badge>
          </div>

          <div className="profile-header-info">
            <Title level={2} className="profile-name">
              {getDisplayName()}
            </Title>
            <Text
              className="profile-subtitle"
              style={{ color: getProfileColor() }}
            >
              {getSubtitle()}
            </Text>

            <Space className="profile-header-actions" wrap>
              <Upload
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleSignatureUpload}
                accept=".jpg,.jpeg,.png"
              >
                <Button
                  icon={<CameraOutlined />}
                  loading={uploading}
                  style={{
                    borderColor: getProfileColor(),
                    color: getProfileColor(),
                  }}
                >
                  {uploading ? "Încărcare..." : "Încarcă semnătură"}
                </Button>
              </Upload>

              <Button danger icon={<LogoutOutlined />} onClick={handleSignOut}>
                Ieși din cont
              </Button>
            </Space>
          </div>
        </div>
      </Card>

      {/* Profile Information */}
      <Card
        className="profile-info-card"
        title={
          <Space>
            <IdcardOutlined style={{ color: getProfileColor() }} />
            Informații personale
          </Space>
        }
      >
        {renderProfileInfo()}
      </Card>

      {/* Privacy Policy Link */}
      <div className="profile-footer">
        <a
          href={process.env.REACT_APP_PRIVACY}
          target="_blank"
          rel="noopener noreferrer"
          className="privacy-link"
        >
          Politica de confidențialitate
        </a>
      </div>
    </div>
  );
};

export default ModernProfile;
